import moment from 'moment';
import { LOCALSTATUS as formLocalStatus, STATUS as formStatus } from '../../config';
import { sendStorageEvent } from './sendEvent';

const { synchronized, sending, to_send, tmp_form, updated } = formLocalStatus;
const { submitted, saved } = formStatus;

export class ManageForms {
  constructor({ formHandler, formRepo, imageHandler, imageRepo, report, answerManager, messenger }) {
    this.formHandler = formHandler;
    this.formRepo = formRepo;
    this.imageHandler = imageHandler;
    this.imageRepo = imageRepo;
    this.report = report;
    this.messenger = messenger;
    this.messenger.addListener('syncForms', () => this.syncForms());
    this.messenger.addListener('syncImages', () => this.syncImages());
    this.messenger.addListener('syncAllAnswers', () => this.syncAllFormAnswers());
    this.messenger.addListener('resetImages', () => this.resetImages());
    this.messenger.addListener('resetForms', () => this.resetForms());
    this.answerManager = answerManager;
    this.currentImageUpload = 0;
    this.maxUpload = 10;
    this.currentFormId = 0;
  }

  getFormFromLocal(formId) {
    return this.formRepo.get(formId);
  }

  async saveFormInLocal(formId, options = { localStatus: synchronized }) {
    const form = await this.formHandler.get(formId);
    const { localStatus } = options;
    form.localStatus = localStatus;
    if (localStatus === tmp_form) await this.deleteTmpForms();
    await this.formRepo.put(formId, form);
  }

  async delete(formId) {
    await this.formRepo.delete(formId);
  }

  formInRepo(formId) {
    return this.formRepo.exists(formId);
  }

  async saveToDistant(formId, form) {
    if (form.status === 2 || form.status === 4) return;
    const currentStatus = form.localStatus;
    await this.setLocalStatus(formId, sending);
    try {
      await this.formHandler.put(formId, form);
      await this.setLocalStatus(formId, synchronized);
      sendStorageEvent(`${formId}-synchronized`);
    } catch (e) {
      console.log('error saveToDistant', e);
      if (e !== 'api_error_inspection') {
        const newForm = await this.formHandler.get(formId);
        if (newForm.status !== 6) {
          await this.setLocalStatus(formId, currentStatus);
        } else {
          await this.setLocalStatus(formId, synchronized);
          sendStorageEvent(`${formId}-synchronized`);
        }
      }
    }
  }

  async setLocalStatus(formId, localStatus) {
    await this.formRepo.saveValue(formId, 'localStatus', localStatus);
  }

  async setStatus(formId, status) {
    await this.formRepo.saveValue(formId, 'status', status);
  }

  getListFromRepo({ status, localStatus }) {
    return this.formRepo.getList({ status, localStatus });
  }

  async saveValue(formId, path, value, questionId, questionType, questionChild = null, questionChiledType = null) {
    await this.formRepo.saveValue(formId, path, value, questionId, questionType, questionChild, questionChiledType);
  }

  async postImage(image) {
    return (await this.imageHandler.post(image.formId, image.image))?.id;
  }

  async syncImage(id) {
    const image = await this.imageRepo.get(id);
    await this.imageRepo.setImageStatus(id, 1);
    try {
      const imageId = await this.postImage(image);
      if (imageId) {
        await this.imageRepo.setImageId(id, imageId);
        await this.imageRepo.setImageStatus(imageId, 2);
        await this.formRepo.saveValue(image?.formId, image.path, imageId);
        await this.answerManager.saveAnswerToRepo({
          formId: image?.formId,
          path: image.path,
          value: imageId,
          status: 0,
          questionId: image.questionId,
          questionType: image.questionType,
        });
      }
    } catch (e) {
      console.log('error update image', e); // eslint-disable-line no-console
      await this.imageRepo.setImageStatus(id, 0);
      return;
    } finally {
      this.freeOneImageFromMaxSimultaneousImagesUploads();
    }
  }

  freeOneImageFromMaxSimultaneousImagesUploads() {
    this.currentImageUpload -= 1;
  }

  limitSimultaneousImagesUploads(imageIds) {
    const accepted = imageIds.slice(0, this.maxUpload - this.currentImageUpload);
    this.currentImageUpload = accepted.length;
    return accepted;
  }

  async syncImages() {
    const imageIds = this.limitSimultaneousImagesUploads(await this.imageRepo.getImagesIds({ status: 0 }));
    await Promise.all(imageIds.map((id) => this.syncImage(id)));
  }

  async syncForms() {
    const formsToSend = await this.getListFromRepo({ localStatus: to_send });
    const formsFinished = await this.formsReadyToBeSync(formsToSend);
    await Promise.all(formsFinished.map((form) => this.saveToDistant(form.id, form)));
  }

  async syncAllFormAnswers() {
    await this.answerManager.sendAllAnswers();
  }

  async formsReadyToBeSync(forms) {
    const formsFinished = [];
    await Promise.all(
      forms.map(async (f) => {
        if (f.status !== submitted) formsFinished.push(f);
        else if (await this.checkImagesSync(f.id)) {
          // generate the report before push - to be remove in profit of generation on API side
          let content = {};
          if (f?.inspection?.brandId) {
            const { countryId } = f?.inspection;
            const params = { countryId, recordId: f.recordId };
            content = await this.report.processReport(f, 'formsReadyToBeSync', null, params); // get updated content to make sure that we have the right image ids
          } else {
            content = await this.report.processReport(f, 'formsReadyToBeSync'); // get updated content to make sure that we have the right image ids
          }
          const finalizedForm = {
            ...f,
            inspection: {
              ...f.inspection,
              report: content.display,
              damages: content.damages,
            },
          };
          if (
            finalizedForm?.inspection?.report === undefined ||
            finalizedForm?.inspection?.report?.totals === undefined
          ) {
            await this.setStatus(f.id, saved);
            await this.setLocalStatus(f.id, updated);
          } else {
            await this.saveValue(f.id, 'inspection.report', content.display);
            await this.saveValue(f.id, 'inspection.damages', content.damages);
            formsFinished.push(finalizedForm);
          }
        }
      }),
    );
    return formsFinished;
  }

  async submitForm(formId) {
    await this.setStatus(formId, submitted);
    await this.messenger.sendMessage('syncImages', '');
    await this.messenger.sendMessage('syncAllAnswers', '');
    await this.setLocalStatus(formId, to_send);
    this.messenger.sendMessage('syncForms', '');
  }

  async checkImagesSync(formId) {
    return (
      [
        ...(await this.imageRepo.getImagesIds({ formId, status: 0 })),
        ...(await this.imageRepo.getImagesIds({ formId, status: 1 })),
      ].length === 0
    );
  }

  async removeFormDataFromRepo(formId) {
    const imageIds = await this.imageRepo.getImagesIds({ formId });
    await this.imageRepo.deleteList(imageIds);
    await this.delete(formId);
  }

  async getImage(imageId) {
    let image = await this.imageRepo.getImageDataUrl(imageId);
    if (image) return image;
    image = await this.imageHandler.get(imageId);
    return image;
  }

  async saveImage(formId, image, path, questionId, questionType) {
    const previousImageId = await this.imageRepo.getImageIdWithFormIdAndPath({ formId, path });
    if (previousImageId) await this.imageRepo.deleteList([previousImageId]);
    const imageId = await this.imageRepo.saveImage(formId, image, path, questionId, questionType);
    this.messenger.sendMessage('syncImages', '');
    return imageId;
  }

  async deleteImage(imageId) {
    await this.imageRepo.deleteList([imageId]);
  }

  async deleteTmpForms() {
    const tmpForms = await this.formRepo.getIdsList({ localStatus: tmp_form });
    await Promise.all(tmpForms.map((id) => this.delete(id)));
  }

  async saveFormTempory(formId) {
    await this.saveFormInLocal(formId, { localStatus: tmp_form });
  }

  async apiFormIsNewerThanRepo(formId) {
    let repoUpdatedAt;
    try {
      repoUpdatedAt = await this.formRepo.updatedAt(formId); // if form not in repo
    } catch (e) {
      return true;
    }
    const distantUpdatedAt = await this.formHandler.updatedAt(formId);
    if (!distantUpdatedAt) return false;
    if (!repoUpdatedAt) return true;
    return moment(distantUpdatedAt).isAfter(moment(repoUpdatedAt));
  }

  async updateFormIfUpdated(formId) {
    if (await this.apiFormIsNewerThanRepo(formId)) await this.saveFormInLocal(formId);
  }

  async resetImages() {
    const imageIds = await this.imageRepo.getImagesIds({ status: 1 });
    await Promise.all(
      imageIds.map(async (id) => {
        const res = await this.imageRepo.setImageStatus(id, 0);
        return res;
      }),
    );
  }

  async resetForms() {
    const formsToSend = await this.getListFromRepo({ localStatus: sending });
    await Promise.all(
      formsToSend.map(async (form) => {
        const res = await this.setLocalStatus(form.id, to_send);
        return res;
      }),
    );
  }

  async getLocalStatus(formId) {
    const localStatus = await this.formRepo.localStatus(formId);
    return localStatus;
  }
}
